<script setup lang="ts">
import type { NuxtLinkProps } from "#app";

export type BaseNuxtLinkProps = {
  linkPath: string; // The path to the link. If the path starts with "http://", "https://", or "//", it will be treated as an external link.
  colorScheme?: "primary" | "secondary" | "tertiary" | "text-only" | "danger";
  buttonWidth?: "auto" | "full";
  labelAlignment?: "left" | "center";
  disabled?: boolean;
  target?: NuxtLinkProps["target"];
};

const {
  buttonWidth = "auto",
  colorScheme = "primary",
  labelAlignment = "left",
  linkPath,
} = defineProps<BaseNuxtLinkProps>();

const { link, isExternalLink } = useLocaleOrExternalPath(() => linkPath);
</script>

<template>
  <NuxtLink
    :disabled="disabled"
    :to="link"
    :target="target || (isExternalLink ? '_blank' : target)"
    class="inline-block"
  >
    <BaseButton
      is="div"
      v-if="colorScheme !== 'text-only'"
      class="inline-block"
      :disabled="disabled"
      :color-scheme="colorScheme"
      :class="{
        'nuxt-link-button--full': buttonWidth === 'full',
      }"
    >
      <span
        class="nuxt-link-button__label-container"
        :class="labelAlignment === 'center' && 'justify-center'"
      >
        <slot />

        <BaseIcon
          v-if="isExternalLink"
          name="externalLink"
          class="icon"
        />
      </span>
    </BaseButton>

    <span
      v-else
      class="uppercase hover:underline flex items-center gap-1"
    >
      <slot name="leftIcon" />

      <slot />

      <slot name="rightIcon">
        <BaseIcon
          v-if="isExternalLink"
          name="externalLink"
          class="icon"
        />
      </slot>
    </span>
  </NuxtLink>
</template>

<style scoped lang="postcss">
.icon {
  @apply ml-2;
}

.nuxt-link-button {
  &__label-container {
    @apply flex items-center;
  }
}

.nuxt-link-button {
  &--full {
    @apply w-full;
  }
}
</style>
